<template>
  <div id="vue-yacht-tracker">
    <div class="top-banner">
      <button
        class="close-btn"
        aria-label="Close tracker"
        @click="goBack"
      >
        <font-awesome-icon icon="chevron-circle-left" />
      </button>
      <p>{{ raceName }} {{ raceYear }}</p>
    </div>
    <div
      :class="{ active: fleetListActive }"
      class="fleet-container"
    >
      <div>
        <div
          class="fleet-list-header"
          aria-label="Expand fleet list"
          role="button"
          :aria-expanded="fleetListActive"
          @click="
            () => {
              fleetListActive = !fleetListActive;
            }
          "
        >
          <div class="fleet-list-toggle">
            <div class="toggle-pill" />
          </div>
          <h1>
            Fleet
            <span
              class="yacht-count"
            >{{ filteredYachtCount
            }}{{
              isFilterApplied ? ` of total ${totalYachtCount}` : ""
            }}</span>
          </h1>
        </div>
        <div class="fleet-list-filter">
          <button
            aria-label="Toggle filter"
            aria-expanded="showFilter"
            @click="() => (showFilter = !showFilter)"
          >
            Filter
            <font-awesome-icon
              :icon="`chevron-${showFilter ? 'up' : 'down'}`"
            />
          </button>
          <div
            v-if="showFilter"
            class="filter-selects"
          >
            <select
              v-model="selectedCategory"
              class="category-filter"
              aria-label="Race category"
              @change="onCategoryChange"
            >
              <option value>
                All Categories
              </option>
              <option
                v-for="(divList, cat) in categoryList"
                :key="cat"
                :value="cat"
              >
                {{ cat }}
              </option>
            </select>

            <select
              v-if="
                selectedCategory && categoryList[selectedCategory].length > 0
              "
              v-model="selectedDivision"
              class="division-filter"
              aria-label="Race division"
              @change="onDivisionChange"
            >
              <option value>
                All Divisions
              </option>
              <option
                v-for="div in categoryList[selectedCategory]"
                :key="div"
                :value="div"
              >
                {{ div }}
              </option>
            </select>

            <select
              v-model="selectedStatus"
              class="status-filter"
              aria-label="Race Status"
              @change="onStatusChange"
            >
              <option value>
                All Statuses
              </option>
              <option
                v-for="div in statusList"
                :key="div"
                :value="div"
              >
                {{ div }}
              </option>
            </select>

            <div class="filter-checkbox">
              <input
                id="showFavouritesOnly"
                v-model="showFavouritesOnly"
                type="checkbox"
                name="showFavouritesOnly"
                @click="showFavouritesClicked"
              >
              <label for="showFavouritesOnly">Favourites only</label>
            </div>
          </div>
        </div>
        <div class="fleet-list-container">
          <fleet-list
            v-if="!loading"
            :fleet-data.sync="fleetDataFiltered"
            :favourites.sync="favourites"
            @favourite-clicked="toggleFavourite"
          />
        </div>
      </div>
    </div>
    <div
      :style="style"
      class="tracker-container"
    >
      <tracker-map
        v-if="!loading"
        :race-id="raceId"
        :race-time="currentRaceTime"
        :forecast-time="windForcastTime"
        :fleet-data="fleetDataFiltered"
        :options.sync="mapOptions"
        :is-pre-race="raceTime.isPreRace"
      />
      <div
        v-if="!raceTime.isPreRace || raceTime.list.length > 1"
        id="race-time-controls"
      >
        <label
          id="race-time-label"
          for="race-time-selector"
        >Race time {{ timeSinceStart }}</label>
        <input
          id="race-time-selector"
          v-model="raceTime.currentIndex"
          type="range"
          :min="0"
          :max.sync="raceTime.list.length - 1"
          step="1"
          @change="onRaceTimeChange"
          @input="onRaceTimeChange"
        >
        <span id="race-time-passed">{{ totalRaceTime }}</span>
      </div>
      <div
        v-else
        id="race-time-controls"
      >
        <label
          id="race-time-label"
          for="race-time-selector"
        >Pre-race</label>
        <span id="race-time-passed">Live locations</span>
      </div>
      <div id="map-toggles-right">
        <button
          id="zoom-to-yachts"
          class="map-icon is-active"
          aria-label="Fit all yachts on screen"
          @click="zoomToYachts"
        >
          <font-awesome-icon icon="search-location" />
        </button>
        <button
          id="yacht-tracks-toggle"
          :class="{ 'is-active': showAllYachtTracks }"
          class="map-icon"
          @click="toggleYachtTracks"
        >
          Tracks
          <span
            v-if="showAllYachtTracks"
            class="on-indicator"
          >ON</span>
        </button>
        <button
          id="rhumb-line-toggle"
          :class="{ 'is-active': mapOptions.showRhumbLine }"
          class="map-icon"
          @click="toggleRhumbLine"
        >
          Rhumb Line
          <span
            v-if="mapOptions.showRhumbLine"
            class="on-indicator"
          >ON</span>
        </button>
        <button
          id="yacht-name-toggle"
          :class="{ 'is-active': mapOptions.showYachtNames }"
          class="map-icon"
          @click="toggleYachtNames"
        >
          Yacht Names
          <span
            v-if="mapOptions.showYachtNames"
            class="on-indicator"
          >ON</span>
        </button>
        <button
          v-if="raceId !== 167"
          id="wind-toggle"
          :class="{ 'is-active': mapOptions.showWind }"
          class="map-icon"
          aria-label="Toggle wind"
          @click="toggleWind"
        >
          <font-awesome-icon icon="wind" />
          <span
            v-if="mapOptions.showWind"
            class="on-indicator"
          >ON</span>
        </button>
        <div
          v-if="mapOptions.showWind"
          class="map-controls"
        >
          <span class="map-control-title">Wind Forecast</span>
          <button
            :aria-label="`Get forcast for ${windForcastTime - 1}`"
            :disabled="windForcastTime === 0"
            class="map-control-icon"
            @click="
              () => {
                if (windForcastTime > 0) windForcastTime -= 2;
              }
            "
          >
            <font-awesome-icon icon="minus" />
          </button>
          <span class="map-control-info">{{
            windForcastTime | windForcastTimeDisplay
          }}</span>
          <button
            :aria-label="`Get forcast for ${windForcastTime - 1}`"
            :disabled="windForcastTime === 15"
            class="map-control-icon"
            @click="
              () => {
                if (windForcastTime < 15) windForcastTime += 2;
              }
            "
          >
            <font-awesome-icon icon="plus" />
          </button>
        </div>
      </div>

      <div
        v-if="activeYachtId >= 0"
        id="yacht-info-wrapper"
      >
        <div id="yacht-info">
          <yacht-toast :yacht.sync="currentYachtInfo" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

import { library } from '@fortawesome/fontawesome-svg-core'
import {
  faChevronCircleLeft,
  faChevronDown,
  faChevronUp,
  faSearchLocation,
  faWind,
  faPlus,
  faMinus,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
library.add(
  faChevronCircleLeft,
  faChevronDown,
  faChevronUp,
  faSearchLocation,
  faWind,
  faPlus,
  faMinus
)

import TrackerMap from './components/TrackerMap/TrackerMap'
import YachtToast from './components/TrackerMap/YachtToast'
import FleetList from './components/FleetList/FleetList'

import { EventBus } from './eventBus'

String.prototype.replaceAt = function(index, replacement) {
  return (
    this.substr(0, index) +
    replacement +
    this.substr(index + replacement.length)
  )
}

export default {
  name: 'VueYachtTracker',
  filters: {
    windForcastTimeDisplay(value) {
      if (value === 0) return 'Now'
      if (value > 0) return `+${value}hr`
    },
  },
  components: {
    TrackerMap,
    YachtToast,
    FleetList,
    FontAwesomeIcon,
  },
  data() {
    return {
      raceId: '',
      raceName: '',
      raceYear: '',
      isLiveRace: false,
      mapOptions: {
        center: [0, 0],
        zoom: 5,
        showRhumbLine: true,
        showWind: true,
        showAllYachts: true,
        showYachtNames: true,
        fleetBounds: { minLon: null, minLat: null, maxLon: null, maxLat: null },
        showBuoys: true
      },
      style: {
        position: 'relative',
        width: '100%',
        height: '100vh',
      },
      fleetData: [],
      selectedCategory: '',
      selectedDivision: '',
      selectedStatus: '',
      favourites: [] /* a list of yacht ids */,
      showFavouritesOnly: false,
      showAllYachtTracks: false,
      showFilter: false,
      loading: true,
      errored: false,
      raceTime: {
        min: '',
        max: '',
        currentIndex: 0,
        list: [],
        isPreRace: false,
      },
      fleetListActive: false,
      activeYachtId: -1,
      windForcastTime: 0,
    }
  },
  computed: {
    fleetDataFiltered: function() {
      let filteredYachts = [...this.fleetData] /* deep clone */

      if (
        !(this.selectedCategory === '') &&
        !(this.selectedCategory === 'All')
      ) {
        if (
          !(this.selectedDivision === '') &&
          !(this.selectedDivision === 'All')
        ) {
          const catAndDiv = (cat) =>
            cat.Name === this.selectedCategory &&
            cat.Division === this.selectedDivision
          filteredYachts = filteredYachts.filter(
            (yacht) => yacht.Categories.findIndex(catAndDiv) > -1
          )
        }
 else {
          const justCat = (cat) => cat.Name === this.selectedCategory
          filteredYachts = filteredYachts.filter(
            (yacht) => yacht.Categories.findIndex(justCat) > -1
          )
        }
      }

      if (this.selectedStatus == 'Racing') {
        filteredYachts = filteredYachts.filter(
          (yacht) => yacht.Position.IsRacing
        )
      }
      if (this.selectedStatus == 'Retired') {
        filteredYachts = filteredYachts.filter(
          (yacht) => yacht.Position.IsRetired
        )
      }
      if (this.selectedStatus == 'Finished') {
        filteredYachts = filteredYachts.filter(
          (yacht) => yacht.Position.IsFinished
        )
      }

      if (this.showFavouritesOnly) {
        filteredYachts = filteredYachts.filter((yacht) =>
          this.favourites.includes(yacht.RaceEntryId)
        )
      }

      return filteredYachts
    },
    isFilterApplied: function() {
      return (
        this.selectedCategory || this.showFavouritesOnly || this.selectedStatus
      )
    },
    categoryList: function() {
      /* get all categories / divs from fleet list */
      const getCategoryInfo = function(category) {
        return {
          id: category.CategoryId,
          name: category.Name,
          division: category.Division,
        }
      }

      /* flatten array - ie 11 cannot use flat() */
      const allCatDivs = [].concat(
        ...this.fleetData.map((yacht) =>
          yacht.Categories.map((cat) => getCategoryInfo(cat))
        )
      )

      /* get unique category / division combos */
      const uniqueCatDivs = {}
      for (let i = 0; i < allCatDivs.length; i++) {
        /* new category */
        if (!uniqueCatDivs[allCatDivs[i].name]) {
          uniqueCatDivs[allCatDivs[i].name] = []
        }

        /* new division */
        if (
          allCatDivs[i].division !== '' &&
          uniqueCatDivs[allCatDivs[i].name].indexOf(allCatDivs[i].division) ===
            -1
        ) {
          uniqueCatDivs[allCatDivs[i].name] = [
            ...uniqueCatDivs[allCatDivs[i].name],
            allCatDivs[i].division,
          ]
          uniqueCatDivs[
            allCatDivs[i].name
          ].sort() /* sort divisions in order e.g. 1,2,3,4,All */
        }
      }

      return uniqueCatDivs
    },
    statusList() {
      return ['Racing', 'Finished', 'Retired']
    },
    currentRaceTime: function() {
      return this.raceTime.list[this.raceTime.currentIndex]
    },
    timeSinceStart: function() {
      return calcTimeDiff(
        this.raceTime.min,
        this.raceTime.list[this.raceTime.currentIndex]
      )
    },
    totalRaceTime: function() {
      return calcTimeDiff(this.raceTime.min, this.raceTime.max)
    },
    fleetBounds: function() {
      return this.calculateFleetBounds()
    },
    currentYachtInfo: function() {
      return this.getYachtInfo(this.activeYachtId)
    },
    filteredYachtCount: function() {
      return this.fleetDataFiltered.filter((yacht) => !yacht.IsLineHonourRecord)
        .length
    },
    totalYachtCount: function() {
      return this.fleetData.filter((yacht) => !yacht.IsLineHonourRecord).length
    },
  },
  watch: {
    favourites: {
      handler: function(val, oldVal) {
        localStorage.favourites = val
      },
      deep: true,
    },
  },
  mounted() {
    this.raceId = window.YACHT_TRACKER_RACE_ID || ''

    this.raceTime.min = window.YACHT_TRACKER_RACETIME_MIN.toString() || ''
    this.raceTime.max = window.YACHT_TRACKER_RACETIME_MAX.toString() || ''

    this.mapOptions.center[0] = window.YACHT_TRACKER_CENTER_LON || 0
    this.mapOptions.center[1] = window.YACHT_TRACKER_CENTER_LAT || 0
    this.mapOptions.zoom = window.YACHT_TRACKER_ZOOM || 5
    /* There is no wind for Audi Centre Sydney Flinders Islet Race 2022, do not show toggle */
    if (window.YACHT_TRACKER_RACE_ID === 167) {
      this.mapOptions.showWind = false
    }

    this.style.width = window.YACHT_TRACKER_WIDTH || '100%'
    this.style.height = window.YACHT_TRACKER_HEIGHT || '100vh'

    this.raceTime.isPreRace = window.YACHT_TRACKER_IS_PRERACE || false

    this.isLiveRace = window.YACHT_TRACKER_IS_LIVE_RACE || false

    this.raceName = window.YACHT_TRACKER_RACE_NAME || ''
    this.raceYear = window.YACHT_TRACKER_RACE_YEAR || ''

    if (localStorage.favourites) {
      try {
        /* localStorage saves array as comma separated strings (need to convert back to numbers) */
        this.favourites = localStorage.favourites
          .split(',')
          .map((favourite) => parseInt(favourite))
      }
 catch (err) {
        console.error('There was a problem with localStorage.favourites', err)
        this.favourites = []
      }
    }

    this.generateValidRaceTimeList()

    if (this.isLiveRace) {
      this.fetchRaceData()

      this.$nextTick(() => {
        this.raceTime.currentIndex = this.raceTime.list.length - 1

        window.setInterval(() => {
          this.fetchRaceData()
        }, 60000)
      })
    }
 else {
      this.raceTime.currentIndex = 0 /* if not live race, set current time to start */
      this.fetchRaceData(this.currentRaceTime)
    }

    EventBus.$on('yachtRowClicked', (raceEntryId) => {
      this.fleetListActive = false
      this.activeYachtId = raceEntryId
    })

    EventBus.$on('yachtClicked', (raceEntryId) => {
      this.activeYachtId = raceEntryId
    })

    EventBus.$on('clearAllYachtPopups', () => {
      this.activeYachtId = -1
    })

    EventBus.$on('closeYachtInfoClicked', () => {
      this.activeYachtId = -1
    })

    EventBus.$on('showAllYachtTracks', () => {
      this.$nextTick(() => {
        this.showAllYachtTracks = true
      })
    })

    EventBus.$on('clearAllYachtTracks', () => {
      this.$nextTick(() => {
        this.showAllYachtTracks = false
      })
    })

    /* safari mobile fix */
    const vh = window.innerHeight * 0.01
    document.documentElement.style.setProperty('--vh', `${vh}px`)

    window.addEventListener('resize', () => {
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)
    })
    /* !-- safari mobile fix -- */
  },
  methods: {
    goBack() {
      window.history.back()
    },

    getYachtInfo(raceEntryId) {
      if (raceEntryId < 0) return {}

      const yacht = this.fleetData.find(
        (yacht) => yacht.RaceEntryId === raceEntryId
      )
      if (!yacht) return {}

      return yacht
    },

    fetchRaceData(raceTime) {
      if (raceTime === undefined) {
        raceTime = ''
      }

      axios
        .get(
          `${window.BASE_FEEDS_URL}Yachts/${this.raceId}?raceTime=${raceTime}`
        )
        .then((response) => {
          this.fleetData = response.data

          if (
            raceTime === '' &&
            response.data &&
            response.data.length > 0 &&
            response.data[0].RaceTime
          ) {
            const newRaceTime = response.data[0].RaceTime.toString()
            const latestRaceMax = newRaceTime.replace(/[-T:]/g, '')
            if (this.raceTime.max !== latestRaceMax) {
              console.log('new race max')
              this.raceTime.max = latestRaceMax
              this.$nextTick(() => {
                this.generateValidRaceTimeList()
                this.raceTime.currentIndex =
                  this.raceTime.list.length -
                  1 /* set current time to latest */
              })
            }
          }
        })
        .catch((error) => {
          this.errored = true
          // eslint-disable-next-line
          console.log(error);
        })
        .then(() => {
          this.windForcastTime = 0 /* reset wind time */
          if (this.showAllYachtTracks) {
            EventBus.$emit('clearAllYachtTracks')
            EventBus.$emit('showAllYachtTracks')
          } 
          else {
            // if only a single track is showing, clear it
            EventBus.$emit('clearAllYachtTracks')
          }
          this.loading = false
        })
    },

    generateValidRaceTimeList() {
      this.raceTime.list = populateRaceTimeList(
        this.raceTime.min,
        this.raceTime.max
      )
    },

    onRaceTimeChange() {
      if (this._timeout) clearTimeout(this._timeout)

      const that = this
      this._timeout = setTimeout(() => {
        that.fetchRaceData(that.currentRaceTime)
      }, 100)

      EventBus.$emit('clearAllYachtTracks')
    },

    calculateFleetBounds() {
      const bounds = this.fleetData.reduce(
        (boundingBox, boat) => {
          if (!boat.Position) return boundingBox

          let minLat
          let maxLat
          let minLon
          let maxLon

          if (boundingBox.minLat === null) {
            minLat = boat.Position.Latitude
          }
 else {
            minLat =
              boat.Position.Latitude < boundingBox.minLat
                ? boat.Position.Latitude
                : boundingBox.minLat
          }

          if (boundingBox.maxLat === null) {
            maxLat = boat.Position.Latitude
          }
 else {
            maxLat =
              boat.Position.Latitude > boundingBox.maxLat
                ? boat.Position.Latitude
                : boundingBox.maxLat
          }

          if (boundingBox.minLon === null) {
            minLon = boat.Position.Longitude
          }
 else {
            minLon =
              boat.Position.Longitude < boundingBox.minLon
                ? boat.Position.Longitude
                : boundingBox.minLon
          }

          if (boundingBox.maxLon === null) {
            maxLon = boat.Position.Longitude
          }
 else {
            maxLon =
              boat.Position.Longitude > boundingBox.maxLon
                ? boat.Position.Longitude
                : boundingBox.maxLon
          }

          return { minLon, minLat, maxLon, maxLat }
        },
        { minLon: null, minLat: null, maxLon: null, maxLat: null }
      )

      /* add some buffer around the edges with 0.9999 and 1.00001 */
      return {
        minLon: bounds.minLon * 0.9999,
        minLat: bounds.minLat * 1.00001,
        maxLon: bounds.maxLon * 1.0001,
        maxLat: bounds.maxLat * 0.9999,
      }
    },

    onCategoryChange() {
      this.selectedDivision = ''
      EventBus.$emit('clearAllYachtTracks')
    },

    onDivisionChange() {
      EventBus.$emit('clearAllYachtTracks')
    },

    onStatusChange() {
      EventBus.$emit('clearAllYachtTracks')
    },

    toggleFavourite(yacht) {
      if (this.favourites.indexOf(yacht.RaceEntryId) > -1) {
        this.removeYachtFromFavourites(yacht)
      }
 else {
        this.addYachtToFavourites(yacht)
      }
    },

    showFavouritesClicked () {
      EventBus.$emit('clearAllYachtTracks')
    },

    addYachtToFavourites(yacht) {
      this.favourites.push(yacht.RaceEntryId)
    },

    removeYachtFromFavourites(yacht) {
      this.favourites.splice(this.favourites.indexOf(yacht.RaceEntryId), 1)
    },

    toggleYachtTracks() {
      if (!this.showAllYachtTracks) {
        EventBus.$emit('showAllYachtTracks')
      }
      else {
        EventBus.$emit('clearAllYachtTracks')
      }
    },

    toggleRhumbLine() {
      this.mapOptions.showRhumbLine = !this.mapOptions.showRhumbLine
    },

    toggleAllYachts() {
      this.mapOptions.showAllYachts = !this.mapOptions.showAllYachts
    },

    toggleYachtNames() {
      this.mapOptions.showYachtNames = !this.mapOptions.showYachtNames
    },

    toggleWind() {
      this.mapOptions.showWind = !this.mapOptions.showWind
    },

    zoomToYachts() {
      this.mapOptions.fleetBounds = this.calculateFleetBounds()
    },
  },
}

function calcTimeDiff(startTimeString, endTimeString) {
  if (!startTimeString || !endTimeString) return ''
  if (parseInt(startTimeString) >= parseInt(endTimeString)) return ''
  // e.g. a time string is 20181227165000
  const startYear = parseInt(startTimeString.substr(0, 4))
  const startMonth = parseInt(startTimeString.substr(4, 2))
  const startDay = parseInt(startTimeString.substr(6, 2))
  const startHour = parseInt(startTimeString.substr(8, 2))
  const startMinute = parseInt(startTimeString.substr(10, 2))
  const startTime = new Date(
    startYear,
    startMonth - 1,
    startDay,
    startHour,
    startMinute,
    0
  )

  const endYear = parseInt(endTimeString.substr(0, 4))
  const endMonth = parseInt(endTimeString.substr(4, 2))
  const endDay = parseInt(endTimeString.substr(6, 2))
  const endHour = parseInt(endTimeString.substr(8, 2))
  const endMinute = parseInt(endTimeString.substr(10, 2))
  const endTime = new Date(
    endYear,
    endMonth - 1,
    endDay,
    endHour,
    endMinute,
    0
  )

  const diff = endTime - startTime
  const days = diff / 1000 / 60 / 60 / 24 - ((diff / 1000 / 60 / 60 / 24) % 1)
  const hours =
    ((diff / 1000 / 60 / 60 / 24) % 1) * 24 -
    ((((diff / 1000 / 60 / 60 / 24) % 1) * 24) % 1)
  let minutes =
    ((((diff / 1000 / 60 / 60 / 24) % 1) * 24) % 1) * 60 -
    ((((((diff / 1000 / 60 / 60 / 24) % 1) * 24) % 1) * 60) % 1)
  /* round up to 10 */
  minutes = Math.round(minutes / 10) * 10

  return `${('00' + days).slice(-2)}:${('00' + hours).slice(-2)}:${(
    '00' + minutes
  ).slice(-2)}:00`
}

function populateRaceTimeList(minTime, maxTime) {
  const list = []
  list.push(minTime)
  if (parseInt(minTime) < parseInt(maxTime)) {
    let stackOverflowProtection = 0
    while (
      parseInt(list[list.length - 1]) < maxTime &&
      stackOverflowProtection < 10000
    ) {
      stackOverflowProtection++
      var currTime = list[list.length - 1]
      if (currTime % 10000 < 5000) {
        /* add 10 minutes up to 50 minutes */
        const newTime = parseInt(currTime) + 1000
        list.push(newTime.toString())
      }
 else {
        /* reset to zero minutes */
        currTime = currTime.replaceAt(10, '0')
        if (parseInt(currTime.substr(8, 2)) < 23) {
          /* add an hour */
          const newTime = parseInt(currTime) + 10000
          list.push(newTime.toString())
        }
 else {
          /* reset to midnight */
          currTime = currTime.replaceAt(8, '00')
          if (!isLastDayOfMonth(currTime)) {
            /* add a day */
            const newTime = parseInt(currTime) + 1000000
            list.push(newTime.toString())
          }
 else {
            /* reset to first of month */
            currTime = currTime.replaceAt(6, '01')
            if (parseInt(currTime.substr(4, 2)) < 12) {
              /* add a month */
              const newTime = parseInt(currTime) + 100000000
              list.push(newTime.toString())
            }
 else {
              /* reset to first month */
              currTime = currTime.replaceAt(4, '01')
              /* add a year */
              const newTime = parseInt(currTime) + 10000000000
              list.push(newTime.toString())
            }
          }
        }
      }
    }
    console.log('stackOverflowProtection', stackOverflowProtection)
  }
  return list
}

function isLastDayOfMonth(time) {
  const year = parseInt(time.substr(0, 4))
  const month = parseInt(time.substr(4, 2))
  const day = parseInt(time.substr(6, 2))

  if (month === 2 && year % 4 === 0 && day === 29) return true
  if (month === 2 && year % 4 !== 0 && day === 28) return true

  if ([4, 6, 9, 11].includes(month) && day === 30) return true
  if ([1, 2, 3, 5, 7, 8, 10, 12].includes(month) && day === 31) return true

  return false
}
</script>

<style lang="less" scoped>
.top-banner {
  display: none;
}

#vue-yacht-tracker {
  display: flex;
}

#yacht-info-wrapper {
  position: absolute;
  z-index: 3;
  width: 100%;
  bottom: 80px;
  max-width: 600px;
  left: 0;
  #yacht-info {
    padding: 0 20px;
  }
}

.fleet-container {
  width: 300px;
  height: 650px;
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
  overflow-x: hidden;
  background-color: #ebecf0;
  position: relative;

  h1 {
    font-family: Din_Med, Arial;
    font-size: 1.5rem;
    margin: 10px 0;
    text-align: left;
    padding-left: 24px;
  }

  .yacht-count {
    font-family: Din_Reg, Arial;
    color: #878a96;
    font-size: 0.9rem;
    padding-left: 12px;
  }
}

.fleet-list-toggle {
  display: none;
}

#race-time-controls {
  width: 100%;
  z-index: 2;
  position: absolute;
  bottom: 0;
  background-image: linear-gradient(
    to bottom,
    rgba(38, 59, 89, 0),
    rgba(38, 59, 89, 0.7)
  );
  height: 60px;

  font-family: Din_Med, Arial;
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #fff;
  padding: 0 20px;
  box-sizing: border-box;
  #race-time-label {
    position: absolute;
    left: 20px;
    top: 20px;
  }
  #race-time-passed {
    position: absolute;
    right: 20px;
    top: 20px;
  }
}

.fleet-list-filter {
  background-color: #2c3a45;
  display: flex;
  flex-direction: column;
  button {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-color: #4e5b66;
    border: unset;
    height: 48px;
    font-family: Din_Med, Arial;
    text-transform: uppercase;
    color: white;
    font-size: 0.8rem;
  }

  .filter-selects {
    display: flex;
    flex-direction: column;
    padding: 10px 24px;
    color: white;

    select {
      margin: 5px 0;
      display: block;
      font-size: 16px;
      font-family: Din_Reg, Arial;
      color: white;
      line-height: 1.3;
      padding: 0.6em 1.4em 0.5em 0.8em;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
      border: 1px solid #aaa;
      box-shadow: 0 1px 0 1px rgba(0, 0, 0, 0.04);
      border-radius: 0;
      -moz-appearance: none;
      -webkit-appearance: none;
      appearance: none;
      background-color: #2c3a45;
      background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23007CB2%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E");
      background-repeat: no-repeat, repeat;
      background-position: right 0.7em top 50%, 0 0;
      background-size: 0.65em auto, 100%;
    }

    /* SOURCE: https://webdesign.tutsplus.com/tutorials/how-to-make-custom-accessible-checkboxes-and-radio-buttons--cms-32074 */
    /*style wrapper to give some space*/
    .filter-checkbox {
      font-family: Din_Reg, Arial;
      color: white;
      position: relative;
      margin-bottom: 1rem;
      margin-top: 1rem;
    }

    /*style label to give some more space*/
    .filter-checkbox label {
      display: block;
      padding: 0 0 0 36px;
      text-transform: uppercase;
      font-size: 1rem;
    }

    /*style and hide original checkbox*/
    .filter-checkbox input {
      height: 20px;
      left: 0;
      opacity: 0;
      position: absolute;
      top: 0;
      width: 20px;
    }

    /*position new box*/
    .filter-checkbox input + label::before {
      border: 1px solid #aaa;
      content: "";
      height: 20px;
      left: 0;
      position: absolute;
      top: 0;
      width: 20px;
    }

    /*create check symbol with pseudo element*/
    .filter-checkbox input + label::after {
      content: "";
      border: 4px solid white;
      border-left: 0;
      border-top: 0;
      height: 10px;
      left: 6px;
      opacity: 0;
      position: absolute;
      top: 2px;
      transform: rotate(45deg);
      transition: opacity 0.2s ease-in-out;
      width: 6px;
    }

    /*reveal check for 'on' state*/
    .filter-checkbox input:checked + label::after {
      opacity: 1;
    }

    /*focus styles*/
    .filter-checkbox input:focus + label::before {
      box-shadow: 0 0 0 3px #ffbf47;
      outline: 3px solid transparent; /* For Windows high contrast mode. */
    }
  }
}

@media (max-height: 650px) {
  vue-yacht-tracker {
    height: 100vh;
    height: calc(var(--vh, 1vh) * 100); /* safari mobile fix */
  }

  .tracker-container {
    height: 100vh !important; /* still need height on non-mobile */
    height: calc(var(--vh, 1vh) * 100) !important; /* safari mobile fix */
  }
}

@media (min-width: 480px) and (max-height: 650px) {
  .fleet-container {
    height: 100vh !important; /* still need height on non-mobile */
    height: calc(var(--vh, 1vh) * 100) !important; /* safari mobile fix */
  }
}

@media (max-width: 480px) {
  #vue-yacht-tracker {
    height: 100vh;
    height: calc(var(--vh, 1vh) * 100); /* safari mobile fix */
    flex-direction: column;

    .top-banner {
      display: block;
      background-color: rgba(38, 59, 89);
      color: white;
      opacity: 90;
      text-align: center;
      font-family: Din_Med, Arial;
      text-transform: uppercase;
      height: 50px;
      line-height: 25px;

      p {
        margin-left: 10px;
        font-size: 0.8rem;
      }

      .close-btn {
        position: absolute;
        height: 50px;
        width: 50px;
        color: white;
        font-size: 1rem;
        font-family: Din_Reg, Arial;
        line-height: 1.25rem;
        left: 0px;
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        background-color: rgba(38, 59, 89);
        border: unset;
      }
    }
  }

  #yacht-info-wrapper {
    bottom: 120px;
  }

  .tracker-container {
    height: 100vh !important; /* still need height on non-mobile */
    height: calc(var(--vh, 1vh) * 100) !important; /* safari mobile fix */
  }

  .fleet-list-toggle {
    display: block;
    text-align: center;
    .toggle-pill {
      width: 50px;
      height: 8px;
      background-color: #cacaca;
      margin: 12px auto;
      border-radius: 4px;
    }
  }

  .fleet-container {
    overflow: unset;
    position: fixed;
    z-index: 4;
    height: 5vh;
    height: calc(var(--vh, 1vh) * 5); /* safari mobile fix */
    background: white;
    bottom: 0;
    transition: height 0.3s;
    width: 100%;
    border-radius: 20px 20px 0 0;
    box-shadow: 1px -2px 5px 0 rgba(10, 10, 10, 0.1);
    &.active {
      width: 100%;
      position: fixed;
      z-index: 4;
      background: white;
      height: 90vh;
      height: calc(var(--vh, 1vh) * 90); /* safari mobile fix */
      bottom: 0;
    }
    .fleet-list-container {
      background-color: #ebecf0;
      overflow-y: scroll;
      -webkit-overflow-scrolling: touch;
      height: 85vh;
      height: calc(
        var(--vh, 1vh) * 40
      ); /* safari mobile fix */ /* adjust this whenever adding a new row in filter */
      padding-bottom: 200px; /* account for filter being open */
    }
    h1 {
      text-align: center;
      padding-left: 0;
      margin-bottom: 10px;
    }
  }

  #race-time-controls {
    height: 100px;
  }
} /* end media query max width 480px */

#map-toggles-right {
  z-index: 2;
  position: absolute;
  bottom: 150px;
  right: 20px;
  display: flex;
  flex-direction: column;

  .map-icon {
    border: 0;
    background: none;
    width: 50px;
    height: 50px;
    color: rgb(83, 93, 124);
    text-shadow: 0px 1px 4px rgb(80, 80, 80);
    font-family: Din_Med, Arial;
    text-transform: uppercase;
    text-align: center;
    padding: 0; /* safari phone browser text-centering fix  */
    position: relative;
    &.is-active {
      color: #fff;
    }
  }

  .map-controls {
    color: rgba(38, 59, 89);
    width: 50px;
    display: flex;
    flex-direction: column;
    text-align: center;
    padding: 0; /* safari phone browser text-centering fix  */
    .map-control-title {
      color: white;
      font-size: 0.7rem;
      text-shadow: 0px 1px 4px rgb(80, 80, 80);
      text-transform: uppercase;
      font-family: Din_Med, Arial;
      margin-bottom: 10px;
      width: 100%;
    }
    .map-control-icon {
      // color: white;
      color: white;
      background-color: rgba(38, 59, 89, 0.8);
      border: 0;
      height: 30px;
      width: 100%;
      &:first-of-type {
        border-radius: 5px 5px 0 0;
      }
      &:last-of-type {
        border-radius: 0 0 5px 5px;
      }
    }
    .map-control-info {
      color: white;
      background-color: rgba(38, 59, 89, 0.8);
      height: 20px;
      text-transform: uppercase;
      font-size: 0.7rem;
      font-family: Din_Reg, Arial;
      width: 100%;
    }
  }

  #zoom-to-yachts,
  #wind-toggle {
    font-size: 2rem;
    filter: drop-shadow(0 1px 2px rgb(90, 90, 90));
  }

  .on-indicator {
    font-size: 0.5rem;
    position: absolute;
    bottom: 10px;
    right: -10px;
  }
}
</style>

<style lang="less" scoped>
@import "./yachtTrackerSlider.less";
</style>

<style>
@import "./assets/fonts/fonts.css";
@import "../lib/mapbox-gl.css";

.map-container {
  height: 100% !important;
}

.mgl-map-wrapper {
  height: 100% !important;
}

.mapboxgl-map {
  height: 100% !important;
}
</style>
